/**
 * What a voice call on a session is FOR (RFC 0307 3b to 3d).
 *
 * The voice does no work. It writes every final line to the session (3c), it
 * has four tools a person already has in the thread (3b), and it hears what the
 * log says the agent did (3d). Pure over `SessionVoiceDeps`, so the behaviour
 * the RFC promises can be tested without a call, a repo or a window.
 *
 * In core, not in one client, because every surface that can hold a call
 * (the window today, the phone next) needs the same tools and notices; only
 * the audio adapter is per client.
 */

/** A function the voice may call, as a JSON-schema tool. */
export interface VoiceTool {
  name: string
  description: string
  parameters: Record<string, unknown>
}

/**
 * What a call is for, independent of who carries the audio: its instructions,
 * its tools, and where every final line goes. A client adds how it watches the
 * session once the call is live.
 */
export interface VoicePlan {
  instructions: string
  tools: VoiceTool[]
  onToolCall(name: string, args: unknown): Promise<unknown>
  onTranscript(t: { speaker: 'person' | 'voice'; text: string }): void
  onSpeechEnded?(): void
  /**
   * Whether the voice replies on its own when the person stops talking. False
   * under 3e: it speaks only when the call asks it to (`SessionVoiceDeps.say`).
   */
  respondToSpeech?: boolean
  /** The call ended: nothing this plan was waiting to send goes anywhere. */
  close?(): void
}

export interface SessionVoiceState {
  status: 'idle' | 'queued' | 'running' | 'asking'
  goal?: string
  subtasks: Array<{ title: string; status: string }>
  lastReply?: string
  /**
   * What the agent is saying or doing right now, mid-turn: the same live line
   * the window draws. Without it the voice only knew a turn once it ended.
   */
  live?: { kind: 'text' | 'tool'; text: string }
  question?: QuestionSummary
  /**
   * The thread's own conversation, oldest first: what the person typed and
   * what the agent answered, without the call's spoken lines. The voice is
   * the agent to the person, so it has to know what the agent already said.
   */
  recent?: Array<{ by: 'person' | 'agent'; text: string }>
  /**
   * What is on the person's screen and what they pointed at, as the agent
   * reads it each turn (`<view>`, `<holdings>`). Absent when the client has
   * no window to describe.
   */
  screen?: string
  /**
   * Sessions this one handed work to, where the work actually runs. The call's
   * own agent is often idle while one of these is busy.
   */
  others?: Array<
    { sessionId: string; title: string } & Omit<SessionVoiceState, 'others'>
  >
}

export interface QuestionSummary {
  interactionId: string
  prompt: string
  options: string[]
  recommended?: string
  /** Every part, when the question asks more than one thing. */
  parts?: Array<{ prompt: string; options: string[] }>
}

export interface SessionVoiceDeps {
  sessionTitle: () => string
  /** Write one final line of the call; resolves to its log entry. */
  appendLine(line: { by: 'person' | 'voice'; text: string }): Promise<string>
  /** Queue a run on a person's line already on the log (`relaySpokenLine`). */
  relay(entryId: string, sessionId?: string): Promise<void>
  state(): SessionVoiceState
  /** What the person's screen shows now; '' when there is nothing to add. */
  screen?(): Promise<string>
  /**
   * Answer a question at home or in a followed session. `parts` carries one
   * answer per part, in order, for a question with several.
   */
  answer(
    text: string,
    sessionId?: string,
    parts?: readonly string[],
  ): Promise<boolean>
  /** Stop the agent's running turn (`cancelRun`). */
  stopAgent(sessionId?: string): Promise<void>
  /** How long `tell_agent` waits for the person's line to be transcribed. */
  transcriptWaitMs?: number
  /**
   * How long the person must be quiet before what they said goes to the agent
   * (3e). The last line goes as the prompt; the lines before it ride along as
   * context, so a thought said in three breaths is one turn, not three.
   */
  relayQuietMs?: number
  /** Waits between attempts when sending a line to the agent fails. */
  relayRetryMs?: number[]
  /**
   * Have the voice speak now, following `instruction`. Under 3e the voice
   * does not reply to the person on its own, so the call asks it: an
   * acknowledgement once a line has gone to the agent, a waiting question.
   */
  say?(instruction: string): void
  /**
   * Hand the call back to the World agent (RFC 0336 3d): "back to World".
   * Given only where there is a World agent to go back to; resolves false when
   * the call is already on it.
   */
  backToWorld?(): Promise<boolean>
}

/** The tool a call gets when it can be handed back to the World agent. */
export const BACK_TO_WORLD_TOOL: VoiceTool = {
  name: 'back_to_world',
  description:
    'Hand this call back to the World agent. Only when the person asks for it: "back to World", "take me back to the World agent". What they said is not sent to this session.',
  parameters: { type: 'object', properties: {}, additionalProperties: false },
}

export const VOICE_TOOLS: VoiceTool[] = [
  {
    name: 'tell_agent',
    description:
      'Everything the person says already reaches this call’s agent on its own, a moment after they stop. Call this only to send what they just said to one of the sessions in session_state.others instead, when they clearly address the work running there, with its sessionId.',
    parameters: {
      type: 'object',
      properties: { sessionId: { type: 'string' } },
      additionalProperties: false,
    },
  },
  {
    name: 'session_state',
    description:
      'Read where the work is right now: its status, what it is doing this moment (`live`), the sessions it handed work to (`others`), its goal and subtasks, any question it is waiting on, the recent conversation (`recent`) and what is on the person’s screen (`screen`). Use it only to say in one line what is happening while the work runs, or to ask a waiting question.',
    parameters: { type: 'object', properties: {}, additionalProperties: false },
  },
  {
    name: 'answer_question',
    description:
      'Answer the question the agent is waiting on, with the person’s answer. Use an option’s label when they picked one. When the question has several parts, also pass answers: one per part, in order, and ask for any part they missed first. Pass sessionId only for a session listed in session_state.others; omit it for this call’s home session.',
    parameters: {
      type: 'object',
      properties: {
        answer: { type: 'string' },
        answers: { type: 'array', items: { type: 'string' } },
        sessionId: { type: 'string' },
      },
      required: ['answer'],
      additionalProperties: false,
    },
  },
  {
    name: 'stop_agent',
    description:
      'Stop the agent’s running turn, the work itself. Only when the person names the work: "stop the agent", "stop working on that", "cancel the build". A bare "stop", "wait" or "hold on" is them interrupting you: stop speaking and do not call this. Pass sessionId only for a session listed in session_state.others when the work they mean runs there; omit it for this call’s home session.',
    parameters: {
      type: 'object',
      properties: { sessionId: { type: 'string' } },
      additionalProperties: false,
    },
  },
]

export function voiceInstructions(
  sessionTitle: string,
  opts: { backToWorld?: boolean } = {},
): string {
  return [
    `You are the voice of the agent working in the session "${sessionTitle}", on a call with the person. The call can be put through to another session; when you are told so, you are that session's voice from then on, and session_state's \`title\` always names the session you speak for. Speak in the first person, as the agent: "I'm on it", "I've added the toggle". Never mention another agent, a relay, or passing anything on: to the person there is one of you.`,
    'Everything the person says reaches the agent on its own, a moment after they stop speaking. You do not answer it. The agent does the thinking and the work, and you speak its replies. Never answer about the work, the code, a document or what is on their screen from your own knowledge.',
    'You do not reply to the person on your own. You speak only when the call asks you to: to ask the agent’s question, or to say the agent’s reply. Say exactly what you are asked to say, and nothing about the work of your own.',
    'When you are told the agent finished its turn, say its reply to the person as your own, close to word for word. Leave out code, ids and file paths. If it is long, say the first part and pause, so they can cut in.',
    'While the agent works, if they ask what is happening, call session_state and say in one line what it is doing now. Nothing more.',
    'When you are told the agent has a question for them, ask it plainly as your own, with the recommendation if there is one. When they answer, call answer_question, with the question’s sessionId if it came from a session in others.',
    'A bare "stop", "wait" or "hold on" means stop talking. Call stop_agent only when they name the work: "stop the agent", "cancel that build".',
    'Call tell_agent only when they clearly address the work running in one of the sessions in others; everything else already reaches the agent.',
    ...(opts.backToWorld
      ? [
          'When they ask to go back to the World agent ("back to World"), call back_to_world. You will then be told who you are talking to.',
        ]
      : []),
    'Say nothing you did not hear from the person or read from the session. Speak the person’s language.',
  ].join(' ')
}

/**
 * The call's plan: instructions, tools, and where every line goes.
 *
 * `tell_agent` relays the person's latest line that was not relayed yet. The
 * model can call it before that line's transcript has landed, so it waits a
 * moment for one rather than relaying an older aside.
 */
export function buildVoicePlan(deps: SessionVoiceDeps): VoicePlan {
  const waitMs = deps.transcriptWaitMs ?? 4000
  /** The person's lines, in order, as their entries are written. */
  const lines: Array<Promise<string>> = []
  const relayed = new Set<string>()
  let nextLine: (() => void) | null = null
  /**
   * Utterances the person finished that have no transcript yet. The model
   * can call tell_agent the moment they stop talking, before the words are
   * transcribed; relaying then would send an older aside instead.
   */
  let awaiting = 0
  const quietMs = deps.relayQuietMs ?? 1500
  /** The home relay waiting for the person to go quiet (3e). */
  let homeRelay: ReturnType<typeof setTimeout> | null = null
  const holdHomeRelay = () => {
    if (homeRelay) clearTimeout(homeRelay)
    homeRelay = null
  }
  /** The call hung up: a timer or a late transcript sends nothing after it. */
  let closed = false
  /** What they have said since the last thing went anywhere, in order. */
  let batch: Array<{ entry: Promise<string>; text: string }> = []
  /** A home send in progress, so an addressed send waits for its decision. */
  let inFlight: Promise<void> | null = null
  /**
   * Until when a line that lands is already spoken for. A tool that answered or
   * stopped can run before the words it acted on are transcribed; without
   * this, those words arrived afterwards and went to the agent as a new turn.
   */
  let consumedUntil = 0
  /** Take what they have said so far out of the home relay: a tool used it. */
  const consumeBatch = () => {
    holdHomeRelay()
    for (const line of batch)
      void line.entry.then(
        (id) => relayed.add(id),
        () => {},
      )
    batch = []
    consumedUntil = Date.now() + waitMs
  }
  const RETRY_MS = deps.relayRetryMs ?? [1000, 3000]
  const relayWithRetry = async (id: string) => {
    for (let attempt = 0; ; attempt++) {
      try {
        await deps.relay(id)
        return true
      } catch (error) {
        const wait = RETRY_MS[attempt]
        if (wait === undefined || closed) {
          console.warn('[voice] could not send a line to the agent:', error)
          return false
        }
        await new Promise((r) => setTimeout(r, wait))
      }
    }
  }
  const sendHome = async () => {
    homeRelay = null
    if (closed) return
    const pending = batch
    batch = []
    // "Hmm?", "um": a sound, not something to act on. Sent alone, each one was
    // a whole agent turn answering nothing. A real line said before or after
    // it in the same breath still goes; the sounds ride along as context.
    const real = pending.filter((l) => !isFiller(l.text))
    const last = real[real.length - 1]
    if (!last) return
    const said = last.text
    const state = deps.state()
    // A waiting question takes their words as its answer, not as a new turn:
    // sent as a message, the question would stay open and the agent stuck.
    if (state.question) {
      if (!state.question.parts && (await deps.answer(said))) {
        for (const l of pending)
          void l.entry.then(
            (id) => relayed.add(id),
            () => {},
          )
        return
      }
      deps.say?.(
        `They answered the waiting question: "${said}". Call answer_question with it${state.question.parts ? ', one answer per part, asking for any part they missed' : ''}.`,
      )
      return
    }
    const asking = state.others?.find((o) => o.question)
    if (asking) {
      deps.say?.(
        `They answered the question waiting in "${asking.title}": "${said}". Call answer_question with sessionId ${asking.sessionId}.`,
      )
      return
    }
    // "Stop the agent" stops it; it is not also the next instruction.
    if (isStopRequest(said)) {
      await deps.stopAgent()
      deps.say?.('You stopped the work. Say only "Stopped."')
      return
    }
    const id = await last.entry.catch(() => '')
    if (!id || relayed.has(id) || closed) return
    // Reserved before the first await on the send, so an addressed send that
    // looks now sees it taken, and one line never goes to two agents.
    relayed.add(id)
    // No acknowledgement: asked to say "On it", the voice added words of its
    // own, and the screen already shows the agent working.
    if (await relayWithRetry(id)) return
    relayed.delete(id)
    deps.say?.(
      'Tell the person in one short sentence that what they just said did not reach the agent, and ask them to say it again.',
    )
  }

  const latestUnrelayed = async (): Promise<string | null> => {
    for (let i = lines.length - 1; i >= 0; i--) {
      const id = await (lines[i] as Promise<string>).catch(() => '')
      if (!id) continue
      return relayed.has(id) ? null : id
    }
    return null
  }

  /** Until the next person line lands, or the wait runs out. */
  const nextPersonLine = () =>
    new Promise<void>((resolve) => {
      const t = setTimeout(resolve, waitMs)
      nextLine = () => {
        clearTimeout(t)
        resolve()
      }
    }).finally(() => {
      nextLine = null
    })

  const tools: Record<string, (args: unknown) => Promise<unknown>> = {
    async tell_agent(args) {
      const requested = (args as { sessionId?: unknown } | null)?.sessionId
      const targetId = typeof requested === 'string' ? requested.trim() : ''
      if (
        targetId &&
        !deps.state().others?.some((o) => o.sessionId === targetId)
      )
        return { sent: false, reason: 'session is not followed by this call' }
      // This line goes where they addressed it, not home as well. A home send
      // already deciding finishes first, so the two cannot both take it.
      holdHomeRelay()
      if (inFlight) await inFlight
      // What they just said is still being transcribed: wait for it, so the
      // line relayed is the one they meant, not the one before it.
      const deadline = Date.now() + waitMs
      while (awaiting > 0 && Date.now() < deadline) await nextPersonLine()
      // Their words never came. Relaying the latest line we DO have would send
      // an older aside as the instruction, so refuse and let the voice ask
      // again. Reset, so a cough does not make every later relay wait too.
      if (awaiting > 0) {
        awaiting = 0
        return {
          sent: false,
          reason:
            'what they just said was not transcribed; ask them to say it again',
        }
      }
      let id = await latestUnrelayed()
      if (!id) {
        await nextPersonLine()
        id = await latestUnrelayed()
      }
      if (!id) return { sent: false, reason: 'nothing new from the person' }
      relayed.add(id)
      try {
        if (targetId) await deps.relay(id, targetId)
        else await deps.relay(id)
      } catch (error) {
        relayed.delete(id)
        throw error
      }
      const state = deps.state()
      const status = targetId
        ? state.others?.find((o) => o.sessionId === targetId)?.status
        : state.status
      return {
        sent: true,
        agent: status === 'idle' ? 'starting' : 'busy; it will read this next',
      }
    },
    async session_state() {
      // The title is read now, not when the call began: a put-through moves
      // the call to another session (RFC 0336 S8).
      const state = { ...deps.state(), title: deps.sessionTitle() }
      const screen = (await deps.screen?.().catch(() => '')) ?? ''
      return screen ? { ...state, screen } : state
    },
    async answer_question(args) {
      const a = (args ?? {}) as {
        answer?: unknown
        answers?: unknown
        sessionId?: unknown
      }
      // Their answer settles the question; it is not also a new turn, even if
      // its words are still being transcribed.
      consumeBatch()
      const answer = String(a.answer ?? '').trim()
      const parts = Array.isArray(a.answers) ? a.answers.map(String) : []
      const sessionId =
        typeof a.sessionId === 'string' ? a.sessionId.trim() : ''
      const state = deps.state()
      if (sessionId && !state.others?.some((o) => o.sessionId === sessionId))
        return {
          answered: false,
          reason: 'session is not followed by this call',
        }
      const question = sessionId
        ? state.others?.find((o) => o.sessionId === sessionId)?.question
        : state.question
      if (!question)
        return { answered: false, reason: 'no question is waiting' }
      if (!answer) return { answered: false, reason: 'empty answer' }
      if (question.parts && parts.length !== question.parts.length)
        return {
          answered: false,
          reason: `this question has ${question.parts.length} parts; pass answers, one per part, in order`,
        }
      return {
        answered: await (question.parts
          ? deps.answer(answer, sessionId || undefined, parts)
          : sessionId
            ? deps.answer(answer, sessionId)
            : deps.answer(answer)),
      }
    },
    async back_to_world() {
      // The words were the request; they are not also the agent's next turn.
      consumeBatch()
      if (!deps.backToWorld) return { handed: false, reason: 'no World agent' }
      const handed = await deps.backToWorld()
      return handed
        ? { handed: true }
        : { handed: false, reason: 'this call is already with the World agent' }
    },
    async stop_agent(args) {
      // "Stop the agent" stops it; it is not also sent as the next instruction,
      // even if its words land after the stop.
      consumeBatch()
      const requested = (args as { sessionId?: unknown } | null)?.sessionId
      const sessionId = typeof requested === 'string' ? requested.trim() : ''
      if (
        sessionId &&
        !deps.state().others?.some((o) => o.sessionId === sessionId)
      )
        return {
          stopped: false,
          reason: 'session is not followed by this call',
        }
      if (sessionId) await deps.stopAgent(sessionId)
      else await deps.stopAgent()
      return { stopped: true }
    },
  }

  return {
    instructions: voiceInstructions(deps.sessionTitle(), {
      backToWorld: deps.backToWorld !== undefined,
    }),
    tools: deps.backToWorld
      ? [...VOICE_TOOLS, BACK_TO_WORLD_TOOL]
      : VOICE_TOOLS,
    respondToSpeech: false,
    async onToolCall(name, args) {
      const tool = tools[name]
      if (!tool) return { error: `no tool ${name}` }
      return tool(args)
    },
    onSpeechEnded() {
      awaiting++
    },
    close() {
      closed = true
      holdHomeRelay()
      batch = []
      nextLine?.()
    },
    onTranscript(t) {
      if (closed) return
      const entry = deps.appendLine({ by: t.speaker, text: t.text })
      if (t.speaker === 'person') {
        awaiting = Math.max(0, awaiting - 1)
        lines.push(entry)
        // Words a tool already acted on (an answer, a stop) are spoken for.
        const spokenFor = Date.now() < consumedUntil
        if (!spokenFor) batch.push({ entry, text: t.text })
        void entry.then(
          (id) => {
            nextLine?.()
            if (spokenFor) {
              relayed.add(id)
              return
            }
            if (closed) return
            // 3e: what they said goes to the agent once they have gone quiet.
            holdHomeRelay()
            homeRelay = setTimeout(() => {
              const sending = sendHome()
              inFlight = sending
              void sending.finally(() => {
                if (inFlight === sending) inFlight = null
              })
            }, quietMs)
          },
          () => {},
        )
      }
      void entry.catch((err: unknown) =>
        console.warn('[voice] could not write a spoken line:', err),
      )
    },
  }
}

const FILLERS = new Set([
  'hmm',
  'hm',
  'mm',
  'mmm',
  'um',
  'umm',
  'uh',
  'uhh',
  'erm',
  'er',
  'ah',
  'oh',
  'huh',
  'eh',
])

/**
 * The person asking to stop the work, not to stop the voice: a stop word with
 * the work named. A bare "stop" or "wait" is an interruption, and the voice
 * has already gone quiet for it.
 */
export function isStopRequest(text: string): boolean {
  const t = text.toLowerCase()
  return (
    /\b(stop|cancel|halt|abort)\b/.test(t) &&
    /\b(agent|work|working|build|turn|run|that|it|everything)\b/.test(t)
  )
}

/**
 * A line made only of sounds: "Hmm?", "um, uh". At most three words, every
 * one a sound. Not "yes" or "okay": those answer what the agent just asked,
 * and holding them back left the person agreeing to nobody.
 */
export function isFiller(text: string): boolean {
  const words = text
    .toLowerCase()
    .replace(/[^\p{L}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean)
  return (
    words.length > 0 && words.length <= 3 && words.every((w) => FILLERS.has(w))
  )
}

/**
 * How much of a finished reply the voice is handed. Long enough for a whole
 * explanation: cutting it short is what made the voice summarise an answer
 * the person could read in full in the thread.
 */
const REPLY_CHARS = 8000

/**
 * What the voice should hear when the session changes (RFC 0307 3d): a new
 * question, a turn that ended, a subtask that went blocked. The app watches;
 * the voice decides when to say it.
 */
export function voiceNotices(
  prev: SessionVoiceState,
  next: SessionVoiceState,
): string[] {
  const out: string[] = []
  if (
    next.question &&
    next.question.interactionId !== prev.question?.interactionId
  )
    out.push(questionNotice(next.question))
  const wasWorking = prev.status === 'running' || prev.status === 'queued'
  if (wasWorking && next.status === 'idle') {
    out.push(
      next.lastReply
        ? `The agent finished its turn. Its reply, in full: ${next.lastReply.slice(0, REPLY_CHARS)}\n\nSay it to the person as your own words, close to word for word. Leave out code, ids and file paths. If it is long, say the first part and pause so they can cut in.`
        : 'The agent finished its turn without a reply. Tell the person in one line that it is done.',
    )
  }
  const before = new Map(prev.subtasks.map((t) => [t.title, t.status]))
  for (const t of next.subtasks) {
    if (t.status === 'blocked' && before.get(t.title) !== 'blocked')
      out.push(`A subtask is blocked: ${t.title}. Tell the person.`)
  }
  // The sessions the work was handed to: the same notices, said as theirs.
  // One newly followed brings only a question still waiting: its finished
  // turns are history, but the question is holding its agent up now.
  for (const o of next.others ?? []) {
    const was = prev.others?.find((p) => p.sessionId === o.sessionId)
    if (!was) {
      if (o.question)
        out.push(
          `In "${o.title}" (sessionId: ${o.sessionId}), a session the work was handed to: ${questionNotice(o.question)}`,
        )
      continue
    }
    const { sessionId: _id, title, ...now } = o
    const { sessionId: _was, title: _t, ...then } = was
    for (const n of voiceNotices(then, now))
      out.push(
        `In "${title}" (sessionId: ${o.sessionId}), a session the work was handed to: ${n}`,
      )
  }
  return out
}

/** A question the agent is waiting on, said to the voice to ask (3d). */
export function questionNotice(q: QuestionSummary): string {
  if (q.parts)
    return [
      `The agent is waiting on a question in ${q.parts.length} parts.`,
      ...q.parts.map(
        (p, i) =>
          `${i + 1}) ${p.prompt}${p.options.length ? ` Options: ${p.options.join('; ')}.` : ''}`,
      ),
      q.recommended ? `It recommends: ${q.recommended}.` : '',
      'Ask the person at the next pause, as your own question, and get an answer to every part.',
    ]
      .filter(Boolean)
      .join(' ')
  return [
    `The agent is waiting on a question: ${q.prompt}`,
    q.options.length ? `Options: ${q.options.join('; ')}.` : '',
    q.recommended ? `It recommends: ${q.recommended}.` : '',
    'Ask the person at the next pause, as your own question.',
  ]
    .filter(Boolean)
    .join(' ')
}

/**
 * Mid-turn text the agent wrote (RFC 0307 3d), said once it has settled. For
 * the voice's awareness: it mentions it only if it answers what the person
 * asked, or they ask, and never reads it out.
 */
export function liveNotice(text: string): string {
  return `The agent, still working, wrote: ${text.slice(0, 1200)} This is for your awareness. Mention it only if it answers what the person asked or they ask; do not read it out.`
}
